import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PeopleIcon from '@mui/icons-material/People'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import BubbleChartIcon from '@mui/icons-material/BubbleChart'
import { Link, useNavigate } from 'react-router-dom'
import {
  useOwnedCollections,
  useSharedCollections,
  useCreateCollection,
  useRenameCollection,
  useToggleDashboard,
  useDeleteCollection,
  useCollectionPins,
} from '~/api/hooks/useCollections'
import { PageHeader } from '~/components/Navigation/PageHeader'
import { CollectionRenameDialog } from '~/components/Collections/CollectionRenameDialog'
import { CollectionDeleteConfirmDialog } from '~/components/Collections/CollectionDeleteConfirmDialog'
import { CollectionShareDialog } from '~/components/Collections/CollectionShareDialog'
import { errorString, getInlineMessage } from '~/errors/handleApiError'
import type { CaseCollection, VCollection } from '~/api/endpoints/collections'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

interface Props {
  sharedView: boolean
}

export function CollectionList({ sharedView }: Props) {
  const navigate = useNavigate()
  const owned = useOwnedCollections()
  const shared = useSharedCollections()
  const list = sharedView ? shared : owned
  const create = useCreateCollection()
  const rename = useRenameCollection()
  const toggle = useToggleDashboard()
  const del = useDeleteCollection()

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newIsDashboard, setNewIsDashboard] = useState(false)

  const [renameTarget, setRenameTarget] = useState<CaseCollection | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CaseCollection | null>(null)
  const [shareTarget, setShareTarget] = useState<VCollection | null>(null)

  // Pin count for the delete-confirm Dialog. Auto-disables (enabled: false) when no
  // delete is in progress; reuses the per-collection cache key so opening from the
  // CollectionItem page first hits the cache.
  const pinsForDelete = useCollectionPins(deleteTarget?.id ?? null)
  const deletePinCount = deleteTarget ? (pinsForDelete.data?.length ?? null) : null

  const isLoaded = !list.isLoading && !list.isError
  const isEmpty = isLoaded && (list.data ?? []).length === 0

  return (
    <Box sx={{ p: 4 }}>
      <PageHeader
        title={sharedView ? DIALOGS.sharedCollectionsTitle : DIALOGS.collectionsTitle}
        actions={
          !sharedView ? (
            <Button onClick={() => setCreateOpen(true)}>{DIALOGS.createButton}</Button>
          ) : undefined
        }
      />
      <Tabs
        value={sharedView ? 'shared' : 'owner'}
        onChange={(_, v) => navigate(v === 'shared' ? '/collections/shared' : '/collections')}
        aria-label={DIALOGS.collectionsTabsAria}
        sx={{ mb: 2 }}
      >
        <Tab value="owner" label={DIALOGS.ownedTab} />
        <Tab value="shared" label={DIALOGS.sharedTab} />
      </Tabs>
      {list.isLoading ? <Typography>{DIALOGS.loading}</Typography> : null}
      {list.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {DIALOGS.couldNotLoadCollections}
        </Alert>
      ) : null}
      {!sharedView && toggle.error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => toggle.reset()}>
          {errorString(toggle.error) ?? DIALOGS.couldNotUpdateDashboard}
        </Alert>
      ) : null}

      {isEmpty ? (
        <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <BubbleChartIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography color="text.secondary">
            {sharedView ? DIALOGS.noCollectionsShared : DIALOGS.noCollectionsOwned}
          </Typography>
        </Box>
      ) : null}

      <List>
        {(list.data ?? []).map((c) => (
          <CollectionRow
            key={c.id}
            collection={c}
            sharedView={sharedView}
            onToggleDashboard={() =>
              toggle.mutate({ collectionId: c.id, isDashboard: !c.is_dashboard })
            }
            onRenameOpen={() => setRenameTarget(c)}
            onShareOpen={() => setShareTarget(c)}
            onDeleteOpen={() => setDeleteTarget(c)}
          />
        ))}
      </List>

      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false)
          create.reset()
        }}
      >
        <DialogTitle>{DIALOGS.newCollectionTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, minWidth: 320 }}>
            <TextField
              label={DIALOGS.name}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <label>
              <input
                type="checkbox"
                checked={newIsDashboard}
                onChange={(e) => setNewIsDashboard(e.target.checked)}
              />{' '}
              {DIALOGS.markDashboard}
            </label>
          </Stack>
          {create.error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getInlineMessage(create.error) ?? DIALOGS.couldNotCreateCollection}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false)
              create.reset()
            }}
          >
            {DIALOGS.cancel}
          </Button>
          <Button
            onClick={async () => {
              await create.mutateAsync({ name: newName, isDashboard: newIsDashboard })
              setCreateOpen(false)
              setNewName('')
              setNewIsDashboard(false)
            }}
            disabled={!newName || create.isPending}
          >
            {DIALOGS.createButton}
          </Button>
        </DialogActions>
      </Dialog>

      <CollectionRenameDialog
        open={renameTarget !== null}
        currentName={renameTarget?.name ?? ''}
        isSubmitting={rename.isPending}
        error={errorString(rename.error)}
        onClose={() => {
          setRenameTarget(null)
          rename.reset()
        }}
        onSubmit={async (name) => {
          if (!renameTarget) return
          await rename.mutateAsync({ collectionId: renameTarget.id, name })
          setRenameTarget(null)
        }}
      />

      <CollectionDeleteConfirmDialog
        open={deleteTarget !== null}
        collectionName={deleteTarget?.name ?? ''}
        pinCount={deletePinCount}
        isSubmitting={del.isPending}
        error={errorString(del.error)}
        onClose={() => {
          setDeleteTarget(null)
          del.reset()
        }}
        onConfirm={async () => {
          if (!deleteTarget) return
          await del.mutateAsync({ collectionId: deleteTarget.id })
          setDeleteTarget(null)
        }}
      />

      {shareTarget && (
        <CollectionShareDialog
          open={shareTarget !== null}
          onClose={() => setShareTarget(null)}
          collection={shareTarget}
        />
      )}
    </Box>
  )
}

interface RowProps {
  collection: VCollection
  sharedView: boolean
  onToggleDashboard: () => void
  onRenameOpen: () => void
  onShareOpen: () => void
  onDeleteOpen: () => void
}

function CollectionRow({
  collection: c,
  sharedView,
  onToggleDashboard,
  onRenameOpen,
  onShareOpen,
  onDeleteOpen,
}: RowProps) {
  const isDash = !!c.is_dashboard
  const PinIcon = isDash ? PushPinIcon : PushPinOutlinedIcon
  const dashLabel = isDash ? DIALOGS.unmarkDashboard : DIALOGS.markDashboard
  const showShareIndicator = !sharedView && c.share_count > 0
  const hasIndicator = isDash || showShareIndicator
  return (
    <ListItem
      disablePadding
      secondaryAction={
        sharedView ? (
          <Tooltip title={DIALOGS.openCollection}>
            <IconButton
              size="small"
              edge="end"
              aria-label={DIALOGS.openCollection}
              component={Link}
              to={`/collections/${c.id}`}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={dashLabel}>
              <span>
                <IconButton
                  size="small"
                  aria-label={dashLabel}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onToggleDashboard()
                  }}
                >
                  <PinIcon fontSize="small" color={isDash ? 'primary' : 'inherit'} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={DIALOGS.rename}>
              <IconButton
                size="small"
                aria-label={DIALOGS.rename}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onRenameOpen()
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={DIALOGS.shareCollection}>
              <IconButton
                size="small"
                aria-label={DIALOGS.shareCollection}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onShareOpen()
                }}
              >
                <PeopleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isDash ? DIALOGS.removeAsDashboardFirst : DIALOGS.delete}>
              <span>
                <IconButton
                  size="small"
                  aria-label={DIALOGS.delete}
                  aria-disabled={isDash}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isDash) return
                    onDeleteOpen()
                  }}
                >
                  <DeleteIcon fontSize="small" color={isDash ? 'disabled' : 'inherit'} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={DIALOGS.openCollection}>
              <IconButton
                size="small"
                edge="end"
                aria-label={DIALOGS.openCollection}
                component={Link}
                to={`/collections/${c.id}`}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }
    >
      <ListItemButton component={Link} to={`/collections/${c.id}`}>
        {hasIndicator ? (
          <ListItemIcon>
            <Stack direction="row" spacing={0.5}>
              {isDash ? (
                <Tooltip title={DIALOGS.dashboardCollection}>
                  <PushPinIcon fontSize="small" color="primary" />
                </Tooltip>
              ) : null}
              {showShareIndicator ? (
                <Tooltip title={DIALOGS.sharedWithCount(c.share_count)}>
                  <PeopleIcon
                    fontSize="small"
                    aria-label={DIALOGS.sharedWithCount(c.share_count)}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={c.name}
          secondary={
            sharedView
              ? DIALOGS.sharedBy(c.owner_email ?? c.user_id)
              : isDash
                ? DIALOGS.dashboardLabel
                : DIALOGS.collection
          }
        />
      </ListItemButton>
    </ListItem>
  )
}
